import React from 'react';

import {useFeatures} from '@udemy/react-experimentation-provider';
import {HeroBannerSlideLayoutType} from '@udemy/shared-one-udemy-components';

import {CAREER_ACADEMIES} from 'src/lib/experimentation-helper';

import {BannerDataProps, MarketingHeroBannerProps} from '../marketing-hero-banner.react-component';

const WITH_MEMBERSHIP_CMP_DISCOUNT = new Set([
    'mega_deal_visitors',
    'dec_visitors',
    'affiliate_and_bd_deal_visitors',
    'new_user_seo_deal_visitors',
    'sitewide_sale_visitors',
    'resting_deal_visitors',
    'ad_hoc_deal_visitors',
]);

const isSitewidePromoEnabledForBanner = (bannerData: BannerDataProps) => {
    const useCase = bannerData.membership?.use_case;
    return useCase && WITH_MEMBERSHIP_CMP_DISCOUNT.has(useCase);
};

/**
 * HOC that wraps the MarketingHeroBanner component to inject the Career Academies banner.
 * This should only override the CMP banner if there is no sitewide promo.
 * @param WrappedComponent
 * @constructor
 */
export const WithCareerAcademiesBanner = (
    WrappedComponent: React.ComponentType<MarketingHeroBannerProps>,
) => {
    const imageURL = 'https://s.udemycdn.com/career-academies/career-academies-banner.png';
    const CAREER_ACADEMIES_BANNERS: MarketingHeroBannerProps['heroBanners'] = [
        {
            data: {
                id: 0,
                title: gettext('Skills that start careers'),
                title_responsive: gettext('Skills that start careers'),
                subtitle: gettext(
                    "Introducing Career Accelerators – focus on the skills and real-world experience that'll get you noticed.",
                ),
                subtitle_responsive: gettext(
                    "Introducing Career Accelerators – focus on the skills and real-world experience that'll get you noticed.",
                ),
                image: imageURL,
                image_responsive: imageURL,
                cta_1_url: '/career-accelerators/',
                cta_1_text: gettext('Explore All Career Accelerators'),
                cta_1_target: 'self',
                layout: HeroBannerSlideLayoutType.WIDE_DARK,
            },
        },
    ];

    const WithAcademiesWrapper = (defaultProps: MarketingHeroBannerProps) => {
        const {isLoading: isFeaturesLoading, featureVariantAssignments: features} = useFeatures([
            CAREER_ACADEMIES,
        ]);
        const isLoading = defaultProps.isLoading || isFeaturesLoading;

        const isCareerAcademiesEnabled = features.get(CAREER_ACADEMIES)?.configuration?.feature;

        const isSitewidePromoEnabled = defaultProps.heroBanners.some(
            isSitewidePromoEnabledForBanner,
        );

        const withAcademiesProps: MarketingHeroBannerProps = {
            ...defaultProps,
            heroBanners: CAREER_ACADEMIES_BANNERS,
            noticeType: 'career_academies_hardcoded',
            showBannerSearchBar: false,
        };

        const heroBannerOverrideProps =
            isCareerAcademiesEnabled && !isSitewidePromoEnabled ? withAcademiesProps : defaultProps;

        return <WrappedComponent {...heroBannerOverrideProps} isLoading={isLoading} />;
    };

    return WithAcademiesWrapper;
};
